import type { Filters, Requirement } from '../requirements';

/**
 * Deep merges filter objects, ensuring all filter keys are present.
 * This prevents partial filter objects from causing issues.
 *
 * @param defaultFilters - The complete default filters object
 * @param partialFilters - Partial filters to merge in
 * @returns Merged filters with all keys present
 */
export function deepMergeFilters(defaultFilters: Filters, partialFilters: Partial<Filters> | undefined): Filters {
  if (!partialFilters) {
    return defaultFilters;
  }

  const merged = { ...defaultFilters };

  (Object.keys(partialFilters) as Array<keyof Filters>).forEach((key) => {
    if (partialFilters[key]) {
      merged[key] = {
        ...defaultFilters[key],
        ...partialFilters[key],
      };
    }
  });

  return merged;
}

/**
 * Safely merges partial requirement with defaults.
 * Ensures filters are properly deep merged.
 *
 * @param defaultRequirement - The complete default requirement
 * @param partialRequirement - Partial requirement to merge in
 * @returns Merged requirement with proper filter structure
 */
export function mergeRequirement(
  defaultRequirement: Requirement,
  partialRequirement: Partial<Requirement>
): Requirement {
  return {
    ...defaultRequirement,
    ...partialRequirement,
    filters: deepMergeFilters(defaultRequirement.filters, partialRequirement.filters),
  };
}
